/**
 * Logic trang "Rải link lụm tiền" (web). Song song với mobile
 * mobile/src/modules/link-converter/hooks/use-link-converter.ts.
 *
 * Sàn bắt buộc đăng nhập (TikTok Shop — API gắn userId từ session): guest vẫn được
 * XEM hoa hồng, chỉ chặn ở bước lấy link. Bắt đăng nhập ngay lúc dán là mất người
 * dùng ở đúng bước họ còn đang cân nhắc.
 */
import { useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import shopeeApi from "../../api/shopeeApi";
import lazadaApi from "../../api/lazadaApi";
import shopeefoodApi from "../../api/shopeefoodApi";
import tiktokApi from "../../api/tiktokApi";
import riohubAffiliateApi from "../../api/riohubAffiliateApi";
import { LinkSource } from "../../constants/link-source";
import { detectPlatform, extractFirstUrl, PLATFORM_INFO } from "./detect-platform";

/** API chính trả { productInfo }, có nơi bọc thêm một lớp data. */
function pickProductInfo(res) {
    return res?.data?.productInfo ?? res?.data?.data?.productInfo ?? null;
}

/**
 * Thông tin sản phẩm (ảnh/giá/hoa hồng) của ĐÚNG sàn.
 * ShopeeFood không có API hoa hồng theo sản phẩm (link là quán/món) → null.
 */
async function fetchProductInfoByPlatform(platform, url) {
    if (platform === "shopee") return pickProductInfo(await shopeeApi.getProductInfo(url));
    if (platform === "lazada") return pickProductInfo(await lazadaApi.getProductInfo(url));
    if (platform === "tiktok") return pickProductInfo(await tiktokApi.getProductInfo(url));
    return null;
}

/** Gọi API tạo link của ĐÚNG sàn, trả shortLink (null = sàn trả rỗng). */
async function convertByPlatform(platform, url) {
    if (platform === "shopee") {
        const { data } = await shopeeApi.convertLink(url, LinkSource.Converter);
        return data?.data?.batchCustomLink?.[0]?.shortLink ?? null;
    }
    if (platform === "shopeefood") {
        // ShopeeFood không có API tạo link — backend gắn utm vào chính link quán/món.
        const { data } = await shopeefoodApi.getLink(url, LinkSource.Converter);
        return data?.data?.link ?? null;
    }
    if (platform === "tiktok") {
        const { data } = await riohubAffiliateApi.createAffiliateLink({ productUrl: url });
        return data?.data?.affiliateLink ?? null;
    }
    const { data } = await lazadaApi.getLinkAffByUrl(url, {}, LinkSource.Converter);
    return (
        data?.linkAff?.shortLink ??
        data?.data?.shortLink ??
        data?.data?.batchCustomLink?.[0]?.shortLink ??
        null
    );
}

export function useLinkConverter() {
    const { isLogined, userInfo } = useSelector((state) => state.auth);

    const [inputLink, setInputLink] = useState("");
    const [resultLink, setResultLink] = useState("");
    /** Sàn của link KẾT QUẢ (không phải của ô nhập) — để card kết quả hiện đúng logo. */
    const [resultPlatform, setResultPlatform] = useState(null);
    /** Sản phẩm + hoa hồng của link đang xử lý (mọi sàn trừ ShopeeFood). */
    const [preview, setPreview] = useState(null);
    /** Sàn của `preview` — card cần biết để lấy màu nhấn + dòng nhắc đúng sàn. */
    const [previewPlatform, setPreviewPlatform] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const convertingRef = useRef(false);
    const previewSeqRef = useRef(0);

    /**
     * Tra sản phẩm để hiện hoa hồng. KHÔNG ném lỗi — người gọi quyết định có hiển
     * thị hay không, vì với sàn tạo link được thì tra hỏng chỉ mất phần trang trí.
     *
     * @returns message lỗi cho trang, hoặc "" khi không có gì cần báo.
     */
    const loadPreview = useCallback(async (platform, url) => {
        if (platform === "shopeefood") return "";
        // Lượt tra chậm của link CŨ có thể về sau lượt của link mới → đánh số để bỏ
        // qua, không thì user thấy hoa hồng của sản phẩm họ vừa thay ra.
        const seq = ++previewSeqRef.current;
        try {
            const info = await fetchProductInfoByPlatform(platform, url);
            if (seq !== previewSeqRef.current) return "";
            if (!info) return "Không tìm thấy sản phẩm. Vui lòng kiểm tra lại link.";
            setPreview(info);
            setPreviewPlatform(platform);
            return "";
        } catch (err) {
            if (seq !== previewSeqRef.current) return "";
            // Lỗi nghiệp vụ của backend (SP không có hoa hồng…) đã có message cho user.
            return err?.response?.data?.error?.isOperation
                ? err?.response?.data?.message || "Sản phẩm này hiện không được hoàn tiền."
                : "Chưa kiểm tra được link. Vui lòng thử lại.";
        }
    }, []);

    const convert = useCallback(
        async (overrideLink) => {
            const raw = (typeof overrideLink === "string" ? overrideLink : inputLink).trim();
            // Sàn copy cả câu kèm tên SP → chỉ lấy phần link, đừng bắt user tự sửa.
            const trimmed = extractFirstUrl(raw) ?? raw;
            if (!trimmed) {
                setError("Vui lòng dán link sản phẩm");
                return;
            }

            const platform = detectPlatform(trimmed);
            if (!platform) {
                setError("Chỉ hỗ trợ link Shopee, ShopeeFood, Lazada hoặc TikTok Shop");
                return;
            }
            if (convertingRef.current) return;

            convertingRef.current = true;
            setError("");
            setResultLink("");
            setResultPlatform(null);
            setPreview(null);
            setPreviewPlatform(null);
            previewSeqRef.current += 1;
            setLoading(true);

            // Sàn bắt buộc đăng nhập (TikTok): CHƯA tạo link được vì API lấy userId
            // từ session, nhưng endpoint thông tin sản phẩm là public → hiện hoa hồng
            // trước, để dành việc đăng nhập cho lúc user bấm lấy link.
            const guestLocked = !!PLATFORM_INFO[platform].requiresLogin && !isLogined;
            // Chạy SONG SONG với việc tạo link: hai request độc lập, chờ nối tiếp là
            // cộng dồn thời gian chờ của user mà chẳng được gì.
            const previewDone = loadPreview(platform, trimmed);

            try {
                if (guestLocked) {
                    // Card hoa hồng là kết quả DUY NHẤT user thấy → tra hỏng phải báo.
                    const msg = await previewDone;
                    if (msg) setError(msg);
                    return;
                }
                const affLink = await convertByPlatform(platform, trimmed);
                if (affLink) {
                    setResultLink(affLink);
                    setResultPlatform(platform);
                } else {
                    setError("Không thể tạo link. Vui lòng thử lại.");
                }
            } catch (err) {
                setError(err?.response?.data?.message || "Có lỗi xảy ra. Vui lòng thử lại.");
            } finally {
                setLoading(false);
                convertingRef.current = false;
            }
        },
        [inputLink, isLogined, loadPreview]
    );

    const onChangeLink = useCallback(
        (text) => {
            // Dán cả câu → rút gọn ô nhập còn đúng link, để user thấy thứ app sắp gửi đi.
            const url = extractFirstUrl(text);
            const next = url && url !== text.trim() && detectPlatform(url) ? url : text;
            setInputLink(next);
            setError("");
            // Auto-convert khi dán vào ô nhập một link hợp lệ
            if (detectPlatform(next.trim())) convert(next.trim());
        },
        [convert]
    );

    /** Nút xoá (×): dọn luôn kết quả cũ để user không copy nhầm link trước đó. */
    const clearLink = useCallback(() => {
        setInputLink("");
        setResultLink("");
        setResultPlatform(null);
        setPreview(null);
        setPreviewPlatform(null);
        // Chặn lượt tra đang bay: về muộn sau khi user đã xoá thì đừng hiện lại.
        previewSeqRef.current += 1;
        setError("");
    }, []);

    return {
        inputLink,
        onChangeLink,
        clearLink,
        resultLink,
        resultPlatform,
        preview,
        previewPlatform,
        loading,
        error,
        convert,
        isLogined,
        userInfo,
    };
}
